import { Collection, Model } from 'backbone';
import _ from 'underscore';
import Analytics from '../analytics';
import Environment from '../environment';
import HwpTvApi from '../hwpTvApi';
import { getLogger } from '../logging/logger';
import ImageLoader from '../util/imgLoader';
import { COMPARATORS, capitalize } from '../util/utilities';
import BaseRound, { ATTRIBUTES as ROUND_ATTRIBUTES } from './baseRound';
import GameClock from './gameClock';
import fetchData from '../util/fetch';

/**
 * @typedef {import('./leaderboards/leaderboards').LeaderboardConfig} LeaderboardConfig
 */

const EVENTS = Object.freeze({
	initialized: 'initialized',
	gameRound: 'game-round',
	gameComplete: 'game-complete',
});

const ATTRIBUTES = Object.freeze({
	type: 'type',
	gameId: 'gameId',
	name: 'name',
	title: 'title',
	subtitle: 'subtitle',
	instructions: 'instructions',
	scoreType: 'scoreType',
	roundIndex: 'roundIndex',
	contentUrl: 'contentUrl',
	content: 'content',
	contentLimit: 'contentLimit',
	shuffle: 'shuffle',
	repeatQuestions: 'repeatQuestions',
	timeLimit: 'timeLimit',
	tickInterval: 'tickInterval',
	timerOrientation: 'timerOrientation',
	warningTime: 'warningTime',
	fetchProgress: 'fetchProgress',
	countDown: 'countDown',
	countValues: 'countValues',
	pointsPerQuestion: 'pointsPerQuestion',
	score: 'score',
	labels: 'labels',
	endscreen: 'endscreen',
	leaderboard: 'leaderboard',

	// Runtime only properties
	pages: 'pages',
});

const BAR_TIMER_ORIENTATIONS = Object.freeze({
	horizontal: 'horizontal',
	vertical: 'vertical',
});

const HEADER_LABELS = Object.freeze({
	timer: 'timer',
	progress: 'progress',
});

/** @enum {string} */
const SCORE_TYPES = Object.freeze({
	points: 'points',
	timed: 'timed',
});

const ENDSCREEN_OPERANDS = Object.freeze({
	score: 'score',
	percentage: 'percentage',
});

const ENDSCREEN_COMPARATOR = Object.freeze({
	greaterThan: 'greaterThan',
	greaterThanEqualTo: 'greaterThanEqualTo',
	lessThan: 'lessThan',
	lessThanEqualTo: 'lessThanEqualTo',
	equalTo: 'equalTo',
});

export {
	ATTRIBUTES,
	BAR_TIMER_ORIENTATIONS,
	ENDSCREEN_COMPARATOR,
	ENDSCREEN_OPERANDS,
	EVENTS,
	HEADER_LABELS,
	ROUND_ATTRIBUTES,
	SCORE_TYPES,
};

export default class BaseGame extends Model {
	get CN() {
		return BaseGame.name;
	}

	/** @type {typeof BaseRound} */
	get RoundClass() {
		return BaseRound;
	}

	get logger() {
		return getLogger(this.CN);
	}

	defaults() {
		return {
			/** @type {string} The engine identifier */
			[ATTRIBUTES.type]: 'baseGame',
			/** @type {string} Unique id for tracking game data and events against */
			[ATTRIBUTES.gameId]: undefined,
			/** @type {string} A URL of the initial game data JSON file */
			[ATTRIBUTES.contentUrl]: undefined,
			/** @type {string[]} Array of round ids that should be used for this game instance */
			[ATTRIBUTES.content]: undefined,
			/** @type {boolean} Flag to indicate if rounds should be shuffled each time */
			[ATTRIBUTES.shuffle]: false,
			/** @type {number} Allows limiting the number of rounds for a game */
			[ATTRIBUTES.contentLimit]: undefined,
			/** @type {boolean} Flag to indicate if any persisted round data for the user/game should be loaded from the server */
			[ATTRIBUTES.fetchProgress]: true,
			/** @type {boolean} Allows rounds to be repeated during gameplay. Prioritizes the rounds that have been answered the least amount of times */
			[ATTRIBUTES.repeatQuestions]: false,
			/**
			 * @type {number | undefined} The time limit for the game clock.
			 * Undefined == no timing functionality.
			 * Infinity == clock counts to measure time elapsed.
			 * Finite number == clock counds down from timeLimit.
			 * */
			[ATTRIBUTES.timeLimit]: undefined,
			/** @type {number} The time during countdown when the clock should indicate time is running out. */
			[ATTRIBUTES.warningTime]: 0,
			/** @type {'horizontal'|'vertical'} Visual bar timer orientation. */
			[ATTRIBUTES.timerOrientation]: undefined,
			/** @type {number} Used for count down visual. Provide a number to count down from to 0. Providing 0 will skip the countdown */
			[ATTRIBUTES.countDown]: 0,
			/**
			 * @type {string[]} Use for a visual count down but will show text instead of a 3, 2, 1 style countdown.
			 * @example ['Ready', 'Set', 'Go!']
			 * */
			[ATTRIBUTES.countValues]: undefined,
			/** @type {SCORE_TYPES} Indicates if the score should be points based or time based */
			[ATTRIBUTES.scoreType]: SCORE_TYPES.points,
			/** @type {number} The number of points awarded to user per round or question. May be used differently across engines. */
			[ATTRIBUTES.pointsPerQuestion]: 1,
			/** @type {object[]} Array of objects representing pages from the arcade to be used. Typically for endscreen buttons, but sometimes used in other ways. */
			[ATTRIBUTES.pages]: [],
			/** @type {object[]} Array of objects representing endscreen configurations that can be used. */
			[ATTRIBUTES.endscreen]: [],

			/** @type {string} The primary text to be displayed in the game header */
			[ATTRIBUTES.title]: '',
			/** @type {string} The secondary text to be displayed in the game header */
			[ATTRIBUTES.subtitle]: '',
			/** @type {string} The name to be displayed for the game in places like the leaderboard tab names */
			[ATTRIBUTES.name]: '',

			/** @type {object} Object to hold various labels used by the view in the engine. Allows customizing labels through config data. */
			[ATTRIBUTES.labels]: {
				[HEADER_LABELS.timer]: 'TIME',
				[HEADER_LABELS.progress]: 'ROUND',
			},

			/* RUNTIME VALUES */
			/** @type {number} Tracks the current round of the game the user is on */
			[ATTRIBUTES.roundIndex]: -1,
			/** @type {number} The current score for the user. **Not used by most engines** */
			[ATTRIBUTES.score]: 0,
			/** @type {LeaderboardConfig} Configuration data for a games leaderboards */
			[ATTRIBUTES.leaderboard]: {
				sort: undefined, // 'asc' | 'desc'
				template: undefined, // default | rank | bracket
				useNodeLeaderboards: false,
			},
		};
	}

	/** @type {string} Convenience getter for accessing the 'type' attribute */
	get type() {
		return this.get(ATTRIBUTES.type);
	}

	url() {
		return this.get(ATTRIBUTES.contentUrl);
	}

	/**
	 * Convience method to automatically add the 'engine' and 'gameId' properties for
	 * GA4 analytics calls.
	 * @param {String} eventName Name of the event to send to GA
	 * @param {Object} eventData Additional data of event to send to GA
	 */
	sendGA4Event(eventName, eventData) {
		this.logger.log(`[${this.sendGA4Event.name}]`, { eventName, eventData });

		const { type, gameId } = this.attributes;
		Analytics.sendEvent(eventName, { engine: capitalize(type), gameId, ...eventData });
	}

	/**
	 * @param {object} gameData Data needed to populate the game. At least has a contentUrl,
	 * at most has all required data.
	 * @returns {Promise<this>}
	 */
	initialize(gameData) {
		this.logger.log(`[${this.initialize.name}]`, { gameData });

		this.initialized = false;
		this.rounds ??= new Collection([], { model: this.RoundClass });

		return fetchData(this.url())
			.then((response) => {
				this.logger.info(`[fetchData Response]`, { response });

				if (this.parse) {
					return this.parse(response);
				}

				return response;
			})
			.then(
				(response) => {
					this.logger.debug({ response });
					/**
					 * We have an inconsistency in our naming between content sheets. We handle
					 * both cases of a 'data' or 'rounds' array.
					 * */
					const { data, rounds, ...attributes } = response;
					this.set(attributes);

					// Configure game clock
					if (this.get(ATTRIBUTES.timeLimit)) {
						this.clock = new GameClock(
							this.pick(
								ATTRIBUTES.timeLimit,
								ATTRIBUTES.tickInterval,
								ATTRIBUTES.warningTime,
							),
						);
					}

					const { content, shuffle, contentLimit } = this.attributes;
					let roundData = data || rounds;
					this.logger.debug({ content, shuffle, contentLimit, roundData });
					if (content) {
						// Limit content
						roundData = roundData.filter(({ id }) => content.includes(id));
					}
					if (shuffle) {
						// Shuffle content
						roundData = _.shuffle(roundData);
					}
					if (contentLimit) {
						// Cut content down to limit
						roundData.splice(contentLimit);
					}

					this.rounds.set(roundData);
					return this.fetchGameProgress();
				},
				(error) => {
					this.logger.error('[failed to fetch content]', error);
					return Promise.reject(error);
				},
			)
			.finally(() => this.onInitialized());
	}

	parse(response, options) {
		this.logger.log(`[${this.parse.name}]`, { response, options });

		const convertInfinityTimeLimit = (obj) => {
			if (obj[ATTRIBUTES.timeLimit] === new RegExp(Infinity.toString(), 'i')) {
				obj[ATTRIBUTES.timeLimit] = Infinity;
			}
		};

		// Convert game wide timeLimit value
		convertInfinityTimeLimit(response);

		// Extract rounds or deprecated 'data' array to convert round specific timeLimits.
		const { rounds = [], data = [] } = response;
		[...rounds, ...data].forEach(convertInfinityTimeLimit);

		return response;
	}

	/**
	 * This function is called after all data has been set.
	 * Override in child classes to extend initialization functionality.
	 *
	 * @returns {Promise<this>}
	 */
	onInitialized() {
		this.logger.log(`[${this.onInitialized.name}]`);

		this.sendGA4Event('GameInitialized');

		this.initialized = true;
		this.trigger(EVENTS.initialized, this);

		return Promise.resolve(this);
	}

	preloadContent(target) {
		target = target || this.rounds.toJSON();

		const isImageRegex = /\.(jpg|jpeg|png|gif)/;
		const content = {};
		const processObject = (obj) => {
			_.values(obj).forEach((value) => {
				const isImage = isImageRegex.test(value);
				if (_.isString(value) && isImage) {
					content[value] = content[value] + 1 || 1;
				} else if (typeof value === 'object' && value != null) {
					processObject(value);
				}
			});
		};
		processObject(target);
		return ImageLoader.loadImages(Object.keys(content));
	}

	fetchGameProgress() {
		this.logger.log(`[${this.fetchGameProgress.name}]`);

		if (this.get(ATTRIBUTES.fetchProgress) && this.has(ATTRIBUTES.gameId)) {
			const gameId = this.get(ATTRIBUTES.gameId);

			return HwpTvApi.getGameUserAnswer(gameId).then(
				({ data }) => {
					this.syncAnswers(data);
					return data;
				},
				(error) => {
					this.logger.error('[Error fetching game progress]', { error });
					return Promise.reject(error);
				},
			);
		}

		this.logger.warn('[Skipping fetch progress]');
		return Promise.resolve();
	}

	syncAnswers(answersArray) {
		this.logger.log(`[${this.syncAnswers.name}]`, { answersArray, rounds: this.rounds });

		answersArray.forEach(({ answer, questionId, timesAnswered = 0 }) => {
			if (this.rounds.has(questionId)) {
				this.rounds.get(questionId).set({
					[ROUND_ATTRIBUTES.selectedOptionId]: answer,
					[ROUND_ATTRIBUTES.timesAnswered]: timesAnswered,
				});
			}
		});

		const lastAnsweredIndex = this.rounds.findLastIndex((round) =>
			round.has(ROUND_ATTRIBUTES.selectedOptionId),
		);
		const lastRoundIndex = this.rounds.length - 1;

		if (lastAnsweredIndex < lastRoundIndex) {
			/**
			 * Only update our round index if the user has not answered all questions. If they have
			 * answered all questions just start them back at 0.
			 */
			this.roundIndex(lastAnsweredIndex);
		}
	}

	/**
	 * Convience method for finding a round specific attribtue value or defaulting to
	 * getting the value from the main Game object.
	 *
	 * @param {String} attributeName The name of the ATTRIBUTE
	 * @param {Model} round The round model to pull the attribute from.
	 * @returns {any} The attribute value
	 */
	getRoundOrGameAttribute(attributeName, round = this.getCurrentRound()) {
		this.logger.log(`[${this.getRoundOrGameAttribute.name}]`, { attributeName, round });

		const roundAttributes = round?.attributes || {};
		const gameAttributes = this.attributes;

		const { [attributeName]: value } = { ...gameAttributes, ...roundAttributes };
		return value;
	}

	prevRound() {
		this.logger.log(`[${this.prevRound.name}]`);

		const roundIndex = this.roundIndex(Math.max(0, this.roundIndex() - 1));
		const currentRound = this.getCurrentRound();
		if (currentRound) {
			this.trigger(EVENTS.gameRound, currentRound, roundIndex);
			this.sendGA4Event('GameRound', { roundIndex, roundId: currentRound.id });
		}
		return currentRound;
	}

	nextRound() {
		this.logger.log(`[${this.nextRound.name}]`);

		let roundIndex = this.roundIndex(this.roundIndex() + 1);

		if (this.get(ATTRIBUTES.repeatQuestions)) {
			// Sorts by 'timesAnswered' then 'id' to serve up the first least seen question.
			this.rounds.reset(
				this.rounds
					.chain()
					.sortBy(ROUND_ATTRIBUTES.id) // On model itself, no need to extract attribute
					.sortBy((round) => round.get(ROUND_ATTRIBUTES.timesAnswered) || 0)
					.value(),
			);
			roundIndex = this.roundIndex(0);
		}

		const currentRound = this.getCurrentRound();

		const { gameId = '', type = this.type } = this.pick(ATTRIBUTES.gameId, ATTRIBUTES.type);

		if (currentRound) {
			if (this.clock) {
				this.clock.timeLimit = this.getRoundOrGameAttribute(
					ATTRIBUTES.timeLimit,
					currentRound,
				);
			}

			this.trigger(EVENTS.gameRound, currentRound, roundIndex);
			this.sendGA4Event('GameRound', { roundId: currentRound[ROUND_ATTRIBUTES.id] });
		} else {
			const score = this.getScore();
			let possibleScore;
			if (Environment.isHub() || Environment.isInHub()) {
				// Only send possible score if we're in a hub experience to trigger XP calculation
				possibleScore = this.getPossibleScore();
			}
			HwpTvApi.addGameScore(gameId, score, type, possibleScore);
			this.trigger(EVENTS.gameComplete);
			this.sendGA4Event('GameComplete');
		}
		return currentRound;
	}

	getCurrentRound() {
		this.logger.log(`[${this.getCurrentRound.name}]`, this.rounds.length);
		if (this.roundIndex() < 0) {
			this.roundIndex(-1);
			return null;
		}

		if (this.roundIndex() >= this.rounds.length) {
			this.roundIndex(this.rounds.length);
			return null;
		}

		return this.rounds.at(this.roundIndex());
	}

	/**
	 * Gets points for the specificed round and defaults to the currect round. Sets points if the value is provided.
	 * @param {Number} [points] Number of points to add to the current round
	 * @param {Number} [roundIndex] The round index to get points for. Defaults to the current round.
	 * @returns {Number} Points for the current or specifed round
	 */
	roundPoints(points, roundIndex) {
		this.logger.log(`[${this.roundPoints.name}]`, { points, roundIndex });

		// Use provided round index or default to current round index
		roundIndex = roundIndex || this.roundIndex();

		// Get round data and points
		const round = this.rounds.at(roundIndex);
		let roundPoints = this.getCurrentRound().get(ROUND_ATTRIBUTES.selectedOptionId) || 0;

		// Add additional points if supplied
		if (points) {
			roundPoints = points;
			round.set(ROUND_ATTRIBUTES.selectedOptionId, roundPoints);
		}

		return roundPoints;
	}

	addRoundPoints(points = 0) {
		this.logger.log(`[${this.addRoundPoints.name}]`, { points });

		if (!Number.isFinite(points)) {
			this.logger.error('non-finite value passed into addRoundPoints');
			return this.roundPoints();
		}

		// grab our existing score, add points, recall to set current round's points
		const currentPoints = this.roundPoints();
		return this.roundPoints(currentPoints + points);
	}

	pointsPerQuestion() {
		this.logger.log(`[${this.pointsPerQuestion.name}]`);

		return this.getRoundOrGameAttribute(ATTRIBUTES.pointsPerQuestion);
	}

	getScore() {
		this.logger.log(`[${this.getScore.name}]`);

		return this.rounds.reduce(
			(sum, round) =>
				round.has(ROUND_ATTRIBUTES.selectedOptionId)
					? sum + round.get(ROUND_ATTRIBUTES.selectedOptionId)
					: sum,
			0,
		);
	}

	getPossibleScore() {
		return this.rounds.length * this.get(ATTRIBUTES.pointsPerQuestion);
	}

	getCorrectAnswers() {
		return this.getScore();
	}

	/**
	 * Searches through endscreen array for the first endscreen with a passing
	 * condition. If no endscreens match conditions, the first available one will
	 * be returned.
	 * @returns {Object | undefined} The endscreen ID or undefined if none match.
	 */
	getEndscreenData() {
		this.logger.log(`[${this.getEndscreenData.name}]`);

		const { endscreen: endscreens = [] } = this.attributes;
		if (!endscreens.length) {
			return undefined;
		}

		const replacementValues = this.getEndscreenReplacementValues();

		const endscreenData = endscreens.find((data) => {
			const { condition } = data;
			if (!condition) {
				return false;
			}
			const { propertyName, comparator, targetValue } = condition;
			const compareFunction = COMPARATORS[comparator];
			const compareValue = replacementValues[propertyName];

			return compareFunction(compareValue, targetValue);
		});

		// Default to the first available endscreen if none was found.
		return endscreenData || endscreens[0];
	}

	getEndscreenReplacementValues() {
		this.logger.log(`[${this.getEndscreenReplacementValues.name}]`);
		return {
			[ENDSCREEN_OPERANDS.score]: this.getScore(),
			[ENDSCREEN_OPERANDS.percentage]: this.getScore() / this.getPossibleScore(),
		};
	}

	resetGame() {
		this.logger.log(`[${this.resetGame.name}]`);

		this.set(ATTRIBUTES.score, 0);
		this.roundIndex(-1);
		if (this.clock) {
			this.clock.reset();
		}
	}

	roundIndex(value) {
		this.logger.log(`[${this.roundIndex.name}]`);

		if (_.isNumber(parseInt(value)) && !_.isNaN(parseInt(value))) {
			this.set(ATTRIBUTES.roundIndex, parseInt(value));
		}

		return this.get(ATTRIBUTES.roundIndex);
	}

	destroy() {
		this.logger.log(`[${this.destroy.name}]`);

		this.off();

		this.clock?.stop?.();
		this.clock?.off?.();
		this.clock = null;

		this.rounds.each((round) => round?.off?.());
		this.rounds?.clear?.();
		this.rounds?.off?.();
		this.rounds = null;
	}
}
